import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/lib/auth";
import { AnalyticsLoader } from "@/components/AnalyticsLoader";
import { ScrollToTop } from "@/components/ScrollToTop";
import { UTMTracker } from "@/components/UTMTracker";
import { UTMLinkInterceptor } from "@/components/UTMLinkInterceptor";
import { RedirectHandler } from "@/components/RedirectHandler";

// Rotas críticas (entrada do site) — mantidas no bundle inicial.
import PortalLayout from "./pages/portal/PortalLayout.tsx";
import PortalBlog from "./pages/portal/PortalBlog.tsx";
import NotFound from "./pages/NotFound.tsx";

// Demais rotas — code splitting (carregadas sob demanda).
const PortalArtigo = lazy(() => import("./pages/portal/PortalArtigo.tsx"));
const Index = lazy(() => import("./pages/Index.tsx"));
const PortalInsights = lazy(() => import("./pages/portal/PortalInsights.tsx"));
const PortalLearn = lazy(() => import("./pages/portal/PortalLearn.tsx"));
const PortalMateriais = lazy(() => import("./pages/portal/PortalMateriais.tsx"));
const PortalCases = lazy(() => import("./pages/portal/PortalCases.tsx"));
const PortalTools = lazy(() => import("./pages/portal/PortalTools.tsx"));
const PortalEventos = lazy(() => import("./pages/portal/PortalEventos.tsx"));
const PortalRecursos = lazy(() => import("./pages/portal/PortalRecursos.tsx"));
const PortalSobre = lazy(() => import("./pages/portal/PortalSobre.tsx"));
const PortalAutor = lazy(() => import("./pages/portal/PortalAutor.tsx"));
const PortalRecursosLista = lazy(() => import("./pages/portal/PortalRecursosLista.tsx"));
const PortalRecursoDetalhe = lazy(() => import("./pages/portal/PortalRecursoDetalhe.tsx"));
const PortalGlossario = lazy(() => import("./pages/portal/PortalGlossario.tsx"));
const PortalGlossarioDetalhe = lazy(() => import("./pages/portal/PortalGlossarioDetalhe.tsx"));
const PortalCaseDetalhe = lazy(() => import("./pages/portal/PortalCaseDetalhe.tsx"));
const PortalMaterialDetalhe = lazy(() => import("./pages/portal/PortalMaterialDetalhe.tsx"));
const PortalMaterialConteudo = lazy(() => import("./pages/portal/PortalMaterialConteudo.tsx"));
const PortalReportDetalhe = lazy(() => import("./pages/portal/PortalReportDetalhe.tsx"));
const PortalReports = lazy(() => import("./pages/portal/PortalReports.tsx"));
const PortalFerramentaDetalhe = lazy(() => import("./pages/portal/PortalFerramentaDetalhe.tsx"));
const PortalTrilhaDetalhe = lazy(() => import("./pages/portal/PortalTrilhaDetalhe.tsx"));
const PortalNCM = lazy(() => import("./pages/portal/PortalNCM.tsx"));
const PortalNCMDetalhe = lazy(() => import("./pages/portal/PortalNCMDetalhe.tsx"));
const PortalNCMExplorar = lazy(() => import("./pages/portal/PortalNCMExplorar.tsx"));
const PortalNCMCapitulo = lazy(() => import("./pages/portal/PortalNCMCapitulo.tsx"));
const LegacyNCMRedirect = lazy(() => import("./pages/portal/LegacyNCMRedirect.tsx"));
const ClientLogin = lazy(() => import("./pages/portal/ClientLogin.tsx"));
const PortalCategoria = lazy(() => import("./pages/portal/PortalCategoria.tsx"));
const ConfirmarEmail = lazy(() => import("./pages/portal/ConfirmarEmail.tsx"));

// Toasts só são necessários após interação — fora do bundle inicial.
const Sonner = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));
const Toaster = lazy(() => import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })));
const AdminRoutes = lazy(() => import("./AdminRoutes.tsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.tsx"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword.tsx"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      gcTime: 60_000,
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});


/** Fallback neutro: ocupa altura mínima para não gerar layout shift. */
const RouteFallback = () => <div className="min-h-[60vh]" aria-hidden="true" />;

const App = () => {
  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Suspense fallback={null}>
        <Toaster />
        <Sonner />
      </Suspense>
      <BrowserRouter>
        <RedirectHandler />
        <ScrollToTop />
        <UTMTracker />
        <UTMLinkInterceptor />
        <AnalyticsLoader />
        <AuthProvider>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/designsystem" element={<Index />} />

              {/* Password recovery (public) */}
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />

              {/* ADMIN (gated) — bundle separado, nunca carregado no blog */}
              <Route path="/admin/*" element={<AdminRoutes />} />

              {/* PORTAL (public) */}
              <Route path="/" element={<PortalLayout />}>
                <Route index element={<PortalBlog />} />
              <Route path="blog" element={<PortalBlog />} />
                <Route path="confirmar-email" element={<ConfirmarEmail />} />
                <Route path="artigo/:slug" element={<PortalArtigo />} />
                <Route path="categoria/:slug" element={<PortalCategoria />} />
                <Route path="categoria/:catSlug/:slug" element={<PortalArtigo />} />
                <Route path="insights" element={<PortalInsights />} />
                <Route path="reports" element={<PortalReports />} />
                <Route path="report/:slug" element={<PortalReportDetalhe />} />

                <Route path="learn" element={<PortalLearn />} />
                <Route path="trilha/:slug" element={<PortalTrilhaDetalhe />} />
                <Route path="glossario" element={<PortalGlossario />} />
                <Route path="glossario/:slug" element={<PortalGlossarioDetalhe />} />
                <Route path="materiais" element={<PortalMateriais />} />
                <Route path="material/:slug" element={<PortalMaterialDetalhe />} />
                <Route path="cases" element={<PortalCases />} />
                <Route path="case/:slug" element={<PortalCaseDetalhe />} />
                <Route path="tools" element={<PortalTools />} />
                <Route path="ferramenta/:slug" element={<PortalFerramentaDetalhe />} />
                <Route path="ncm" element={<PortalNCM />} />
                <Route path="ncm/explorar/:slug" element={<PortalNCMExplorar />} />
                <Route path="ncm/capitulo/:hs2" element={<PortalNCMCapitulo />} />
                {/* URL canônica hierárquica */}
                <Route path="ncm/:hs2/:hs2Slug/:hs4/:hs4Slug/:code/:slug" element={<PortalNCMDetalhe />} />
                {/* Legacy → redirect 301 (Navigate replace) */}
                <Route path="ncm/:code" element={<LegacyNCMRedirect />} />
                <Route path="ncm/:code/:slug" element={<LegacyNCMRedirect />} />
                <Route path="entrar" element={<ClientLogin />} />
                <Route path="eventos" element={<PortalEventos />} />
                <Route path="recursos" element={<PortalRecursos />} />
                <Route path="sobre" element={<PortalSobre />} />
                <Route path="autor/:slug" element={<PortalAutor />} />
                <Route path="recursos/:subpasta" element={<PortalRecursosLista />} />
                <Route path="recursos/:subpasta/:slug" element={<PortalRecursoDetalhe />} />
              </Route>

              {/* Material fullscreen content (no PortalLayout) */}
              <Route path="/material/:slug/:pageSlug" element={<PortalMaterialConteudo />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
